<template>
  <div class="app-indoorviewer">
    <!-- The main Ivion frame, or the left frame (1) during split view -->
    <IndoorviewFrame
      :id="primaryFrameId"
      :src="primarySrc"
      :splitIndoorViewer="splitIndoorViewer"
    />
    <PulseLoader
      :loading="showPulseLoaderFrame1"
      class="spinner-overview"
      :class="{
        'spinner-overview__split1': splitIndoorViewer
      }"
      color="#1d234f"
    />
    <!-- The right Ivion frame (2) during split view -->
    <IndoorviewFrame
      v-show="splitIndoorViewer"
      :id="secondaryFrameId"
      :src="secondarySrc"
      :splitIndoorViewer="splitIndoorViewer"
    />
    <PulseLoader
      :loading="showPulseLoaderFrame2"
      class="spinner-overview"
      :class="{
        'spinner-overview__split2': splitIndoorViewer
      }"
      color="#1d234f"
    />
    <MeasurementButtons
      v-if="isIndoorviewShowing"
      :splitIndoorViewer="splitIndoorViewer"
      :isFrame1CropActive="isFrame1CropActive"
      :isFrame1MeasurementActive="isFrame1MeasurementActive"
      :isFrame2CropActive="isFrame2CropActive"
      :isFrame2MeasurementActive="isFrame2MeasurementActive"
      :showPulseLoaderFrame1="showPulseLoaderFrame1"
      :showPulseLoaderFrame2="showPulseLoaderFrame2"
      @toolButtonClicked="handleToolButtonClick"
    />
    <CropButtons
      v-if="isIndoorviewShowing && hasIndoorviewEditorAccess"
      :splitIndoorViewer="splitIndoorViewer"
      :isFrame1CropActive="isFrame1CropActive"
      :isFrame1MeasurementActive="isFrame1MeasurementActive"
      :isFrame2CropActive="isFrame2CropActive"
      :isFrame2MeasurementActive="isFrame2MeasurementActive"
      :showPulseLoaderFrame1="showPulseLoaderFrame1"
      :showPulseLoaderFrame2="showPulseLoaderFrame2"
      @toolButtonClicked="handleToolButtonClick"
    />
    <!-- The main Ivion frame floor buttons, or the left frame (1) floor buttons during split view -->
    <FloorButtons
      v-show="
        isBuildingSelected &&
        isIndoorviewShowing &&
        !showPulseLoaderFrame1 &&
        !isFrame1MeasurementActive &&
        !isFrame1CropActive
      "
      :class="{
        'floor-buttons-frame1--split-indoorviewer': splitIndoorViewer
      }"
      :buttons="floorbuttonsFrame1"
      :activeValue="selectedLevelIvFrame1"
      @floorChange="handleClickOnFloorButtonFrame1"
    />
    <!-- The right Ivion frame (2) floor buttons during split view -->
    <FloorButtons
      v-show="
        isBuildingSelected &&
        splitIndoorViewer &&
        !showPulseLoaderFrame2 &&
        !isFrame2MeasurementActive &&
        !isFrame2CropActive
      "
      :buttons="floorbuttonsFrame2"
      :activeValue="selectedLevelIvFrame2"
      @floorChange="handleClickOnFloorButtonFrame2"
    />
    <ViewButtonsIndoor
      v-if="isIndoorviewShowing"
      :disabledViewButton1="showPulseLoaderFrame2"
      :disabledViewButton2="showPulseLoaderFrame1"
      :splitIndoorViewer="splitIndoorViewer"
    />
    <SplitViewButtons
      v-show="
        (primarySrc &&
          instanceLinks.length &&
          isIndoorviewShowing &&
          !showPulseLoaderFrame1 &&
          !hideSplitViewButton) ||
        splitIndoorViewer
      "
      :splitIndoorViewer="splitIndoorViewer"
      @syncButtonClick="handleSplitViewSyncButtonClick"
    />
    <!-- There are new designs for handling the different parts of the menu component, but... -->
    <!-- ...until the old menu below is replaced or repositioned, it needs to be hidden while a feature is active -->
    <IndoorviewerMenu v-show="isIndoorviewShowing && !activeFeature" />
    <!-- The Ivion site names, displayed just below the top bar during split view -->
    <IndoorviewerLabel v-if="isIndoorviewShowing && splitIndoorViewer" />
  </div>
</template>

<script>
import CropButtons from '@/components/indoorviewer/buttons/tool-buttons/CropButtons.vue'
import FloorButtons from '@/components/general/FloorButtons.vue'
import IndoorviewerLabel from '@/components/indoorviewer/IndoorviewerLabel.vue'
import IndoorviewerMenu from '@/components/indoorviewer/menu/IndoorviewerMenu.vue'
import IndoorviewFrame from '@/components/indoorviewer/frame/IndoorviewFrame.vue'
import MeasurementButtons from '@/components/indoorviewer/buttons/tool-buttons/MeasurementButtons.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import SplitViewButtons from '@/components/indoorviewer/buttons/split-view-buttons/SplitViewButtons.vue'
import ViewButtonsIndoor from '@/components/indoorviewer/buttons/view-buttons/ViewButtonsIndoor.vue'
import { useIndoorviewStore } from '@/pinia/indoorview'
import { useFeatures } from '@/pinia/features'
import { getNavvisApiToken } from '@/services/navvis.service'
import { getPoisGeojsonForRealEstate } from '@/services/pois.service'
import { mapState as mapStatePinia } from 'pinia'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { INDOORVIEWER_PREFIX, MAPVIEW_PREFIX } from '@/store/module-prefixes'
import {
  SET_CLICKED_ITEM_TYPE,
  SET_CURRENT_LEVEL_IV_FRAME1,
  SET_CURRENT_LEVEL_IV_FRAME2,
  SET_NAVVIS_API_TOKEN,
  SET_POIS_FOR_SELECTED_REAL_ESTATE_IVION,
  SET_PRIMARY_INSTANCE_ORIENTATION,
  SET_PRIMARY_INSTANCE_POSITION,
  SET_IVION_LINKS,
  SET_SPLIT_INDOORVIEWER,
  SET_STATUS_ON_SHOW_OVERLAY_MODAL
} from '@/store/mutation-types'
import {
  CHANGE_LEVEL,
  CHANGE_LEVEL_IV,
  CHANGE_LEVEL_IV_FRAME2,
  GET_POIS_FOR_SELECTED_BUILDING,
  LOAD_IV_COMPONENTS,
  SET_IV_LEVELS
} from '@/store/action-types'
import { parseJwt } from '@/utils/data'
// import * as Sentry from '@sentry/vue'
import { syncIndoorview } from '@/services/pois.service'

// API for the Ivion site in the main frame, or the left frame (1) during split view
export let ivApi
export let ivInstance

// API for the Ivion site in the right frame (2) during split view
export let ivApi2
export let ivInstance2

export default {
  name: 'IndoorviewerView',
  components: {
    CropButtons,
    FloorButtons,
    IndoorviewFrame,
    IndoorviewerLabel,
    IndoorviewerMenu,
    PulseLoader,
    SplitViewButtons,
    MeasurementButtons,
    ViewButtonsIndoor
  },
  setup() {
    const { setIsIndoorviewCropActive, setIsIndoorviewMeasurementActive } = useIndoorviewStore()
    return {
      setIsIndoorviewCropActive,
      setIsIndoorviewMeasurementActive
    }
  },
  data() {
    return {
      hideSplitViewButton: false,
      initialMovementCounterFrame1: 0, // Used for timing frame 1 movement to initial position
      initialMovementCounterFrame2: 0, // Used for timing frame 2 movement to initial position
      isFrame1CropActive: false,
      isFrame1MeasurementActive: false,
      isFrame2CropActive: false,
      isFrame2MeasurementActive: false,
      isPreviousUrl: false,
      lastUpdatedInstance: null,
      lastUpdatedInstanceTime: null,
      lastUpdatedInstanceUrl: null,
      primaryFrameId: 'indoorviewer-iframe-primary',
      primarySrc: null, // Ivion source url for the main frame, or the left frame (1) during split view
      refreshTimer: undefined,
      secondaryFrameId: 'indoorviewer-iframe-secondary',
      secondarySrc: null, // Ivion source url for the right frame (2) during split view
      showPulseLoaderFrame1: false,
      showPulseLoaderFrame2: false,
      timeBetweenLoad: 0
    }
  },
  computed: {
    ...mapState({
      clickedItemType: (state) => state.clickedItemType,
      roomsForSelectedLevel: (state) => state.roomsForSelectedLevel,
      selectedRoom: (state) => state.selectedRoom,
      selectedLevel: (state) => state.selectedLevel,
      selectedPoi: (state) => state.selectedPoi
    }),
    ...mapState(MAPVIEW_PREFIX, {
      selectedBuilding: (state) => state.selectedBuilding,
      selectedRealEstate: (state) => state.selectedRealEstate
    }),
    ...mapState(INDOORVIEWER_PREFIX, {
      ivLevelsFrame1: (state) => state.ivLevelsFrame1,
      ivLevelsFrame2: (state) => state.ivLevelsFrame2,
      navvisApiToken: (state) => state.navvisApiToken,
      primaryInstance: (state) => state.primaryInstance,
      secondaryInstance: (state) => state.secondaryInstance,
      selectedLevelIvFrame1: (state) => state.selectedLevelIvFrame1,
      selectedLevelIvFrame2: (state) => state.selectedLevelIvFrame2,
      instanceLinks: (state) => state.instanceLinks,
      splitIndoorViewer: (state) => state.splitIndoorViewer,
      poisForSelectedBuildingIvion: (state) => state.poisForSelectedBuildingIvion,
      poisForSelectedRealEstateIvion: (state) => state.poisForSelectedRealEstateIvion
    }),
    ...mapStatePinia(useFeatures, ['featurePath']),
    ...mapStatePinia(useIndoorviewStore, [
      'isIndoorviewCropActive',
      'isIndoorviewMeasurementActive'
    ]),
    ...mapGetters(INDOORVIEWER_PREFIX, {
      navvisInstanceURL: 'navvisInstanceURL',
      navvisInstanceURL2: 'navvisInstanceURL2'
    }),
    ...mapGetters({
      hasIndoorviewEditorAccess: 'hasIndoorviewEditorAccess',
      isIndoorviewShowing: 'isIndoorviewShowing',
      selectedLevelNumber: 'selectedLevelNumber'
    }),
    activeFeature() {
      return this.featurePath.length > 1 ? this.featurePath[1] : null
    },
    floorbuttonsFrame1() {
      if (!this.ivLevelsFrame1) return []
      const floorbuttonsOnIndoorview = []
      this.ivLevelsFrame1.forEach((level) => {
        floorbuttonsOnIndoorview.push({
          character: level.name,
          disabled: !level.hasPanorama,
          emit: level.name
        })
      })
      return floorbuttonsOnIndoorview.reverse()
    },
    floorbuttonsFrame2() {
      if (!this.ivLevelsFrame2) return []
      const floorbuttonsOnIndoorview = []
      this.ivLevelsFrame2.forEach((level) => {
        floorbuttonsOnIndoorview.push({
          character: level.name,
          disabled: !level.hasPanorama,
          emit: level.name
        })
      })
      return floorbuttonsOnIndoorview.reverse()
    },
    isBuildingSelected() {
      return Object.keys(this.selectedBuilding).length > 0
    }
  },
  watch: {
    selectedBuilding: {
      immediate: true,
      async handler(newBuilding, oldBuilding) {
        if (Object.keys(newBuilding).length > 0 && Object.keys(oldBuilding).length > 0) return
        if (!this.isBuildingSelected && this.selectedRealEstate)
          return this.setIvionLinks(this.selectedRealEstate.navvisLinks)
        if (newBuilding === oldBuilding) return
        if (!this.isBuildingSelected && ivApi) {
          // Need to log out from Ivion when changing building
          if (this.primarySrc) {
            await ivApi.AuthenticationService.logout()
            ivApi = null
          }
          if (ivApi2 && this.secondarySrc) {
            await ivApi2.AuthenticationService.logout()
            ivApi2 = null
          }
        }
        this.resetSources()
        if (this.isBuildingSelected) {
          this.setIvionLinks(newBuilding.navvisLinks)
        }
      }
    },
    selectedRealEstate: {
      immediate: true,
      async handler(newRealEstate, oldRealEstate) {
        if (this.clickedItemType !== 'realEstate' && this.clickedItemType !== 'region') return // clickedItemType is set to region for this watch on reload, might be due to a race condition since infodisplay is showing correct information
        if (oldRealEstate !== undefined && newRealEstate === oldRealEstate) return
        if (!newRealEstate && ivApi) {
          // Need to log out from Ivion when changing real estate
          if (this.primarySrc) {
            await ivApi.AuthenticationService.logout()
            ivApi = null
          }
          if (ivApi2 && this.secondarySrc) {
            await ivApi2.AuthenticationService.logout()
            ivApi2 = null
          }
        }
        this.resetSources()
        if (newRealEstate) this.setIvionLinks(newRealEstate.navvisLinks)
      }
    },
    async isIndoorviewShowing(isShowing) {
      if (!isShowing) {
        this.setSplitIndoorviewer({ splitIndoorViewer: false })
        this.showPulseLoaderFrame1 = false
        this.showPulseLoaderFrame2 = false
        return
      }
      const initialPosition = this.getInitialPosition()
      if (initialPosition) this.setPrimaryInstancePosition(initialPosition)
      if (this.isBuildingSelected) {
        // Using selected level from mapview only if it isn't already set for indoorviewer
        if (this.selectedLevelIvFrame1 === null)
          this.setCurrentLevelIvFrame1({ levelNumber: this.selectedLevelNumber })
        if (this.selectedLevelIvFrame1 !== this.selectedLevelNumber) {
          this.setCurrentLevelIvFrame1({ levelNumber: this.selectedLevelNumber })
        }
        if (!this.primarySrc) {
          clearTimeout(this.refreshTimer)
          await this.getPoisForSelectedBuilding()
          await this.createNewInstance(1, this.navvisInstanceURL)
        } else {
          this.moveToInitiallySelectedEntity()
        }
      }
      if (!this.isBuildingSelected) {
        // Using selected level from mapview only if it isn't already set for indoorviewer
        if (this.selectedLevelIvFrame1 === null) this.setCurrentLevelIvFrame1({ levelNumber: 1 })
        if (!this.primarySrc) {
          clearTimeout(this.refreshTimer)
          const realEstatePois = await getPoisGeojsonForRealEstate(this.selectedRealEstate.id)
          this.setPoisForSelectedRealEstateIvion(realEstatePois)
          await this.createNewInstance(1, this.navvisInstanceURL)
        } else {
          this.moveToInitiallySelectedEntity()
        }
      }
    },
    navvisApiToken: {
      immediate: true,
      handler(newToken, oldToken) {
        if (newToken.length && newToken !== oldToken) {
          clearTimeout(this.refreshTimer)
          // The access token has a default lifespan of 30 minutes.
          // Due to inaccuracies in setTimeout, now waiting for expiration -5 minutes until update
          const accessTokenJwt = parseJwt(newToken)
          const tokenExpirationTime = accessTokenJwt.exp // We have interpreted this time as seconds since epoch (local agnostic)
          const currentTime = new Date().getTime() / 1000 // We have interpreted this time as seconds since epoch (local agnostic)
          const timeDifferenceInSeconds = tokenExpirationTime - currentTime
          const expirationTimeMinusFiveMinutes = (timeDifferenceInSeconds - 300) * 1000
          this.refreshTimer = setTimeout(async () => {
            const ivionLoginToken = await getNavvisApiToken(this.navvisInstanceURL)
            if (ivApi) await ivApi.AuthenticationService.updateToken(ivionLoginToken)
            this.setNavvisToken({ token: ivionLoginToken })
          }, expirationTimeMinusFiveMinutes)
        }
      }
    },
    splitIndoorViewer() {
      if (!this.isIndoorviewShowing || !this.splitIndoorViewer) {
        this.showPulseLoaderFrame2 = false
        this.isFrame2CropActive = false
        this.isFrame2MeasurementActive = false
        if (!this.isFrame1CropActive && !this.isFrame2CropActive)
          this.setIsIndoorviewCropActive(false)
        if (!this.isFrame1MeasurementActive && !this.isFrame2MeasurementActive)
          this.setIsIndoorviewMeasurementActive(false)
        return
      }
      if (this.selectedLevelIvFrame1 !== this.selectedLevelIvFrame2)
        this.setCurrentLevelIvFrame2({ levelNumber: this.selectedLevelIvFrame1 })
      this.createNewInstance(2, this.navvisInstanceURL2)
      if (ivApi.view.service.ViewService.primaryView === 'panorama') this.setIvLevels({ frame: 1 })
    },
    // The primaryInstance watch checks for new Ivion site selection (from view buttons)
    primaryInstance() {
      if (!this.isIndoorviewShowing) return
      if (this.splitIndoorViewer) {
        if (this.selectedLevelIvFrame1 !== this.selectedLevelIvFrame2) {
          this.setCurrentLevelIvFrame1({ levelNumber: this.selectedLevelIvFrame2 })
          this.changeLevel({ levelNumber: this.selectedLevelIvFrame2 })
        }
      }
      clearTimeout(this.refreshTimer)
      this.createNewInstance(1, this.navvisInstanceURL)
      setTimeout(() => {
        this.isFrame1CropActive = false
        this.isFrame1MeasurementActive = false
        if (!this.isFrame2CropActive) this.setIsIndoorviewCropActive(false)
        if (!this.isFrame2MeasurementActive) this.setIsIndoorviewMeasurementActive(false)
      }, 5000)
    },
    // The secondaryInstance watch checks for new Ivion site selection (from view buttons)
    secondaryInstance() {
      if (!this.isIndoorviewShowing) return
      if (this.splitIndoorViewer)
        this.setCurrentLevelIvFrame2({ levelNumber: this.selectedLevelIvFrame1 })
      this.createNewInstance(2, this.navvisInstanceURL2)
      setTimeout(() => {
        this.isFrame2CropActive = false
        this.isFrame2MeasurementActive = false
        if (!this.isFrame1CropActive) this.setIsIndoorviewCropActive(false)
        if (!this.isFrame1MeasurementActive) this.setIsIndoorviewMeasurementActive(false)
      }, 5000)
    }
  },
  created() {
    window.onIndoorViewerLoadComplete = this.onIndoorViewerLoadComplete
    window.handleIndoorviewerClick = this.handleIndoorviewerClick
  },
  methods: {
    ...mapMutations({
      setClickedItemType: SET_CLICKED_ITEM_TYPE,
      setInfoMessage: 'setInfoMessage',
      toggleModal: SET_STATUS_ON_SHOW_OVERLAY_MODAL
    }),
    ...mapMutations(INDOORVIEWER_PREFIX, {
      setCurrentLevelIvFrame1: SET_CURRENT_LEVEL_IV_FRAME1,
      setCurrentLevelIvFrame2: SET_CURRENT_LEVEL_IV_FRAME2,
      setNavvisToken: SET_NAVVIS_API_TOKEN,
      setIvionLinks: SET_IVION_LINKS,
      setPoisForSelectedRealEstateIvion: SET_POIS_FOR_SELECTED_REAL_ESTATE_IVION,
      setPrimaryInstancePosition: SET_PRIMARY_INSTANCE_POSITION,
      setPrimaryInstanceOrientation: SET_PRIMARY_INSTANCE_ORIENTATION,
      setSplitIndoorviewer: SET_SPLIT_INDOORVIEWER
    }),
    ...mapActions({
      changeLevel: CHANGE_LEVEL
    }),
    ...mapActions(INDOORVIEWER_PREFIX, {
      changeLevelIv: CHANGE_LEVEL_IV,
      changeLevelIvFrame2: CHANGE_LEVEL_IV_FRAME2,
      getPoisForSelectedBuilding: GET_POIS_FOR_SELECTED_BUILDING,
      loadIv: LOAD_IV_COMPONENTS,
      setIvLevels: SET_IV_LEVELS
    }),
    getInitialPosition() {
      if (this.selectedPoi) {
        const poiCoordinates = {
          x: this.selectedPoi.longitude,
          y: this.selectedPoi.latitude,
          z: this.selectedPoi.elevation
        }
        return poiCoordinates
      } else if (this.selectedRoom) {
        const roomCoordinates = this.selectedRoom.properties.location.coordinates
        const roomPosition = { x: roomCoordinates[0], y: roomCoordinates[1], z: roomCoordinates[2] }
        return roomPosition
      }
      if (!this.isBuildingSelected) return null
      // For level coordinates, instead using a room coordinate if the level has rooms
      // This is an attempt to prevent moving to a center coordinate which might not be placed within the level polygon (due to automatic generation on backend)
      let levelCoordinates
      const roomsForSelectedLevel = this.roomsForSelectedLevel?.data.features
      if (roomsForSelectedLevel?.length) {
        levelCoordinates = roomsForSelectedLevel[0].properties.location.coordinates
      } else {
        levelCoordinates = this.selectedLevel.location.coordinates
      }
      const levelPosition = {
        x: levelCoordinates[0],
        y: levelCoordinates[1],
        z: levelCoordinates[2]
      }
      return levelPosition
    },
    async configIvion(api) {
      /*
      Many frontend config options were removed when the switch from IndoorViewer to Ivion was made.
      Sometimes, information managers might have forgotten to make correct settings in each site, which can lead to errors in the platform.
      Those errors might be related to base map settings (should be turned off), or start view settings (should be panorama för desktop and mobile)
      */
      api.UiApiService.sidebarMenuService.isMenuHidden = true // extra security
      api.ViewApiService.service.ViewService.primaryView = 'panorama'
      api.ViewApiService.service.ViewService.showSecondaryView = true // the Ivion floorplan view
      api.ViewApiService.service.ViewService.mapView.layerMap.visible = true // seems to include both map and scanned image
      if (!api.ViewApiService.service.ViewService.mainView.layerCameraImage)
        await new Promise((resolve) => setTimeout(resolve, 1500))
      if (api.ViewApiService.service.ViewService.mainView.layerCameraImage)
        api.ViewApiService.service.ViewService.mainView.layerCameraImage.visible = true // panoramas
      api.ViewApiService.service.ViewService.mainView.layerLocationMarker.visible = true
      api.ViewApiService.service.ViewService.mapView.layerLocationMarker.visible = true
      api.site.service.activeSite.config.coreInitViewMobile = 'panorama' // works in conjunction with reinit below
      api.DatasetApiService.repository.DatasetRepository.data.forEach(
        (dataset) => (dataset.pointCloudVisible = false)
      )
      api.ViewApiService.service.ViewService.mainView.PointCloudMenuService.config.showPointCloud = false
      api.ViewApiService.service.ViewService.mainView.IV.reinit()
      api.ViewApiService.service.ViewService.mapView.IV.reinit()
    },
    async createNewInstance(frameNumber, url) {
      // Sentry outcommented for now, needs to be updated according to new Sentry version, do we use this?
      /*
      // const transaction = Sentry.startSpan({
      //   name: 'create-new-indoorviewer-instance'
      // })
      */
      if (frameNumber === 1) {
        if (!this.isFrame2CropActive) this.setIsIndoorviewCropActive(false)
        if (!this.isFrame2MeasurementActive) this.setIsIndoorviewMeasurementActive(false)
        this.showPulseLoaderFrame1 = true
        this.initialMovementCounterFrame1 = 2
      }
      if (frameNumber === 2) {
        if (!this.isFrame1CropActive) this.setIsIndoorviewCropActive(false)
        if (!this.isFrame1MeasurementActive) this.setIsIndoorviewMeasurementActive(false)
        this.showPulseLoaderFrame2 = true
        this.initialMovementCounterFrame2 = 2
      }
      this.setIvLevels({ frame: frameNumber, reset: true })
      this.isPreviousUrl = this.lastUpdatedInstanceUrl === url
      this.lastUpdatedInstanceUrl = url
      this.lastUpdatedInstance = frameNumber
      const previouslyUpdatedInstanceTime = this.lastUpdatedInstanceTime
      this.lastUpdatedInstanceTime = new Date().getTime()
      this.timeBetweenLoad = previouslyUpdatedInstanceTime
        ? this.lastUpdatedInstanceTime - previouslyUpdatedInstanceTime
        : 0
      if (this.timeBetweenLoad <= 5000) await new Promise((resolve) => setTimeout(resolve, 5000))
      await this.setIframe(frameNumber, url)
      // transaction.end()
    },
    async getPositionInIframe(matchFrameId, frameToSync) {
      const iframe = document.getElementById(matchFrameId)
      const iv = iframe.contentWindow.IV
      const dir = await iv.getMainView().getViewDir()
      const pano = await iv.getMainView().currentImage
      if (!pano) return null
      const matchApiPosition = frameToSync === 1 ? ivApi2 : ivApi
      const wgs84coordinates = await matchApiPosition.transform.service.localToGlobal.transform(
        pano.location.clone()
      )
      if (!wgs84coordinates) return null
      const position = {
        vlon: dir.lon,
        vlat: dir.lat,
        lon: Math.round(wgs84coordinates.x * 1e9) / 1e9,
        lat: Math.round(wgs84coordinates.y * 1e9) / 1e9,
        z: Math.round(wgs84coordinates.z * 1e2) / 1e2,
        fov: await iv.getMainView().getFov()
      }
      return position
    },
    handleClickOnFloorButtonFrame1(levelNumber) {
      if (this.selectedLevelIvFrame1 === levelNumber) return
      this.changeLevelIv({ levelNumber })
    },
    handleClickOnFloorButtonFrame2(levelNumberFrame2) {
      if (this.selectedLevelIvFrame2 === levelNumberFrame2) return
      this.changeLevelIvFrame2({ levelNumber: levelNumberFrame2 })
    },
    handleIndoorviewerClick() {
      // This function is called from the iframe when a click is made in the viewer
      // For example, it is used to handle tool buttons visibility (switching between our button component and the viewer integrated buttons)
      setTimeout(() => {
        if (
          (ivApi && ivApi.view.service.ViewService.mainView.ModeService.currentMode === 'normal') || // Remove??? (used to be lowercase for IndoorViewer)
          (ivApi && ivApi.view.service.ViewService.mainView.ModeService.currentMode === 'NORMAL') // Needs to be all caps ('NORMAL') for Ivion
        ) {
          this.isFrame1CropActive = false
          this.isFrame1MeasurementActive = false
          if (!this.isFrame2CropActive) this.setIsIndoorviewCropActive(false)
          if (!this.isFrame2MeasurementActive) this.setIsIndoorviewMeasurementActive(false)
        }
        if (
          ivApi &&
          ivApi.view.service.ViewService.primaryView === 'map' &&
          !this.splitIndoorViewer
        ) {
          this.hideSplitViewButton = true
        } else {
          this.hideSplitViewButton = false
        }
        if (
          (ivApi2 &&
            ivApi2.view.service.ViewService.mainView.ModeService.currentMode === 'normal') ||
          (ivApi2 && ivApi2.view.service.ViewService.mainView.ModeService.currentMode === 'NORMAL') // Needs to be all caps ('NORMAL') for Ivion
        ) {
          this.isFrame2CropActive = false
          this.isFrame2MeasurementActive = false
          if (!this.isFrame1CropActive) this.setIsIndoorviewCropActive(false)
          if (!this.isFrame1MeasurementActive) this.setIsIndoorviewMeasurementActive(false)
        }
      }, 200)
    },
    async handleSplitViewSyncButtonClick(frameToSync) {
      if (frameToSync === 1) {
        const currentPosition = await this.getPositionInIframe(this.secondaryFrameId, frameToSync)
        if (currentPosition) this.moveToPosition(this.primaryFrameId, currentPosition)
        if (this.selectedLevelIvFrame1 !== this.selectedLevelIvFrame2) {
          this.setCurrentLevelIvFrame1({ levelNumber: this.selectedLevelIvFrame2 })
          this.changeLevel({ levelNumber: this.selectedLevelIvFrame2 })
        }
        return
      }
      const currentPosition = await this.getPositionInIframe(this.primaryFrameId, frameToSync)
      if (currentPosition) this.moveToPosition(this.secondaryFrameId, currentPosition)
      if (this.selectedLevelIvFrame1 !== this.selectedLevelIvFrame2)
        this.setCurrentLevelIvFrame2({ levelNumber: this.selectedLevelIvFrame1 })
    },
    handleToolButtonClick(frame, type) {
      if (type === 'measurement') localStorage.setItem('ls.iv_unit_key', '"metric"') // Unofficial way to prevent browser setting from overriding instance units. Might have to change to other solution in the future, and it doesn't work for Ivion sites.
      const measurementConfig = {
        defaultViewportSize: {
          left: 0,
          width: 1,
          height: 1,
          bottom: 0
        },
        topViewViewportSize: {
          left: 0,
          width: 0,
          height: 1,
          bottom: 0
        },
        isSidebarCollapsed: true,
        isMagnifyingGlassEnabled: true
      }
      if (frame === 1) {
        if (type === 'crop') {
          ivApi.ui.sidebarMenuService.goToItem(
            ivApi.ui.sidebarMenuService.items.filter((item) => item.title === 'Crop & download')[0]
          )
          this.isFrame1CropActive = true
          this.setIsIndoorviewCropActive(true)
        }
        if (type === 'measurement') {
          localStorage.setItem(
            `ls.${this.navvisInstanceURL}#measurementModeConfig`,
            JSON.stringify(measurementConfig)
          )
          let measurementTitle = ivApi.ui.sidebarMenuService.items.find(
            (item) => item.title === 'Measurements'
          )
            ? 'Measurements'
            : null
          // Now called 'Mark & Measure' for Ivion, or 'Mark & measure' in newer versions
          if (!measurementTitle)
            measurementTitle = ivApi.ui.sidebarMenuService.items.find(
              (item) => item.title === 'Mark & Measure'
            )
              ? 'Mark & Measure'
              : 'Mark & measure'
          ivApi.ui.sidebarMenuService.goToItem(
            ivApi.ui.sidebarMenuService.items.filter((item) => item.title === measurementTitle)[0]
          )
          this.isFrame1MeasurementActive = true
          this.setIsIndoorviewMeasurementActive(true)
        }
      } else if (frame === 2) {
        if (type === 'crop') {
          ivApi2.ui.sidebarMenuService.goToItem(
            ivApi2.ui.sidebarMenuService.items.filter((item) => item.title === 'Crop & download')[0]
          )
          this.isFrame2CropActive = true
          this.setIsIndoorviewCropActive(true)
        }
        if (type === 'measurement') {
          localStorage.setItem(
            `ls.${this.navvisInstanceURL2}#measurementModeConfig`,
            JSON.stringify(measurementConfig)
          )
          let measurementTitle = ivApi.ui.sidebarMenuService.items.find(
            (item) => item.title === 'Measurements'
          )
            ? 'Measurements'
            : null
          // Now called 'Mark & Measure' for Ivion, or 'Mark & measure' in newer versions
          if (!measurementTitle)
            measurementTitle = ivApi.ui.sidebarMenuService.items.find(
              (item) => item.title === 'Mark & Measure'
            )
              ? 'Mark & Measure'
              : 'Mark & measure'
          ivApi2.ui.sidebarMenuService.goToItem(
            ivApi2.ui.sidebarMenuService.items.filter((item) => item.title === measurementTitle)[0]
          )
          this.isFrame2MeasurementActive = true
          this.setIsIndoorviewMeasurementActive(true)
        }
      }
    },
    moveToPosition(frameId, currentPosition) {
      const iframe = document.getElementById(frameId)
      const iv = iframe.contentWindow.IV
      if (!iv) return
      if (!currentPosition.vlon) {
        // when ivionlink - don't use vlon/vlat/fov
        if (!currentPosition.lon || !currentPosition.lat || !currentPosition.z) return
        iv.moveToGeoLocation(
          {
            x: parseFloat(currentPosition.lon),
            y: parseFloat(currentPosition.lat),
            z: parseFloat(currentPosition.z)
          },
          false,
          undefined,
          undefined,
          undefined,
          undefined,
          false,
          false
        )
      } else {
        if (
          !currentPosition.lon ||
          !currentPosition.lat ||
          !currentPosition.z ||
          !currentPosition.fov ||
          !currentPosition.vlon ||
          !currentPosition.vlat
        )
          return
        iv.moveToGeoLocation(
          {
            x: parseFloat(currentPosition.lon),
            y: parseFloat(currentPosition.lat),
            z: parseFloat(currentPosition.z)
          },
          false,
          {
            lon: parseFloat(currentPosition.vlon),
            lat: parseFloat(currentPosition.vlat)
          },
          undefined,
          parseFloat(currentPosition.fov),
          undefined,
          false,
          false
        )
      }
    },
    moveToInitiallySelectedEntity() {
      if (this.selectedPoi) {
        const currentPosition = {
          lon: this.selectedPoi.longitude,
          lat: this.selectedPoi.latitude,
          z: this.selectedPoi.elevation
        }
        this.moveToPosition(this.primaryFrameId, currentPosition)
      } else if (this.selectedRoom) {
        const roomCoordinates = this.selectedRoom.properties.location.coordinates
        const currentPosition = {
          lon: roomCoordinates[0],
          lat: roomCoordinates[1],
          z: roomCoordinates[2]
        }
        this.moveToPosition(this.primaryFrameId, currentPosition)
      } else {
        if (!this.isBuildingSelected) return
        // For level coordinates, instead using a room coordinate if the level has rooms
        // This is an attempt to prevent moving to a center coordinate which might not be placed within the level polygon (due to automatic generation on backend)
        let levelCoordinates
        const roomsForSelectedLevel = this.roomsForSelectedLevel?.data.features
        if (roomsForSelectedLevel?.length) {
          levelCoordinates = roomsForSelectedLevel[0].properties.location.coordinates
        } else {
          levelCoordinates = this.selectedLevel.location.coordinates
        }
        const currentPosition = {
          lon: levelCoordinates[0],
          lat: levelCoordinates[1],
          z: levelCoordinates[2]
        }
        this.moveToPosition(this.primaryFrameId, currentPosition)
      }
    },
    // This function is called from the Ivion iframe (Instance.vue), when the Ivion site api login is complete
    async onIndoorViewerLoadComplete(iv, ivionLoginToken) {
      if (this.lastUpdatedInstance === 1) {
        this.setNavvisToken({ token: ivionLoginToken })
        ivApi = iv.api
        ivInstance = iv
        await this.configIvion(ivApi)
        await this.loadIv() // Currently only setting menu content for the first instance (frame 1) to make the menu (view settings, pois...) fully relate to that frame
        // After each movement/transition is finished, the position and orientation of the instance is updated, and the levels for that location are set.
        await ivApi.ViewApiService.service.ViewService.onTransitionEnd.connect(async (e) => {
          const position = ivApi.transform.service.localToGlobal.transform(e.image.location.clone())
          const orientation = await iv.getMainView().getViewDir()
          this.setPrimaryInstanceOrientation(orientation)
          position.x = Math.round(position.x * 1e9) / 1e9
          position.y = Math.round(position.y * 1e9) / 1e9
          position.z = Math.round(position.z * 1e2) / 1e2
          this.setPrimaryInstancePosition(position)
          if (ivApi.view.service.ViewService.primaryView === 'panorama')
            this.setIvLevels({ frame: 1 })
          if (this.initialMovementCounterFrame1 > 0) {
            if (this.initialMovementCounterFrame1 === 1) {
              this.initialMovementCounterFrame1 = this.initialMovementCounterFrame1 - 1
              if (!this.splitIndoorViewer) this.moveToInitiallySelectedEntity()
              if (this.splitIndoorViewer) this.handleSplitViewSyncButtonClick(1)
              setTimeout(() => {
                this.showPulseLoaderFrame1 = false
              }, 2500)
            }
            if (this.initialMovementCounterFrame1 > 0)
              this.initialMovementCounterFrame1 = this.initialMovementCounterFrame1 - 1
          }
        })
        await ivApi.poi.service.onPoiSave.connect(async (poi) => {
          // Todo: Is a new poisForSelectedBuildingIvion fetch necessary to make sure frontend has latest backend data???
          try {
            const siteUrl = `https://${this.primarySrc.substring(10)}`
            await syncIndoorview({
              navVisId: poi.id,
              navVisLink:
                siteUrl.charAt(siteUrl.length - 1) === '/'
                  ? siteUrl.substring(0, siteUrl.length - 1)
                  : siteUrl
            })
            setTimeout(async () => {
              if (this.isBuildingSelected) {
                this.getPoisForSelectedBuilding()
                return
              }
              const realEstatePois = await getPoisGeojsonForRealEstate(this.selectedRealEstate.id)
              this.setPoisForSelectedRealEstateIvion(realEstatePois)
            }, 5000)
            this.setInfoMessage({
              type: 'success',
              message: this.$t('general__text__poi-change-may-take-time')
            })
          } catch (e) {
            this.setInfoMessage({
              type: 'error',
              message: this.$t('general__text__something-went-wrong-could-not-save-poi')
            })
          }
        })
        /*
        await ivApi.poi.service.onPoiDelete.connect(async (poi) => {
          // Avoid/Do not use this function without verifying that it only affects 1 poi at a time.
          // Has been put in comments because it works very differently depending on site and other unclear reasons
        })
        */
      } else if (this.lastUpdatedInstance === 2) {
        // Additional Ivion timeout for error prevention
        await new Promise((resolve) => setTimeout(resolve, 1500))
        ivApi2 = iv.api // Type: ApiInterface
        ivInstance2 = iv // Type: ApiInterface
        await this.configIvion(ivApi2)
        // After each movement/transition is finished, the position and orientation of the instance is updated, and the levels for that location are set.
        await ivApi2.ViewApiService.service.ViewService.onTransitionEnd.connect(async () => {
          if (ivApi2.view.service.ViewService.primaryView === 'panorama')
            this.setIvLevels({ frame: 2 })
          if (this.initialMovementCounterFrame2 > 0) {
            if (this.initialMovementCounterFrame2 === 1) {
              this.initialMovementCounterFrame2 = this.initialMovementCounterFrame2 - 1
              this.handleSplitViewSyncButtonClick(2)
              setTimeout(() => {
                this.showPulseLoaderFrame2 = false
              }, 2500)
            }
            if (this.initialMovementCounterFrame2 > 0)
              this.initialMovementCounterFrame2 = this.initialMovementCounterFrame2 - 1
          }
        })
      }
    },
    resetSources() {
      this.primarySrc = null
      this.secondarySrc = null
      this.setCurrentLevelIvFrame1({ levelNumber: null })
      this.setCurrentLevelIvFrame2({ levelNumber: null })
    },
    // The setIframe function triggers loading of a new Ivion site (see Instance.vue)
    async setIframe(frameNumber, url) {
      // Need to set to null to remove iframe and create new instead of just rerender
      if (frameNumber === 1) {
        this.primarySrc = null
      } else if (frameNumber === 2) {
        this.secondarySrc = null
      }
      // Delay so the iframe is fully removed before src is set
      await new Promise((resolve) => setTimeout(resolve, 1500))
      // Url configparams do not affect Ivion links, IM:s need to hide the base map in the Ivion site settings
      // Ivion start location can't be set here for an Ivion site (only controlled by IM in the site settings)
      const formattedUrl = url ? `/instance/${url.substr(8)}`.slice(0, -1) : null
      if (frameNumber === 1) {
        this.primarySrc = formattedUrl
      } else if (frameNumber === 2) {
        if (this.selectedLevelIvFrame2 === null) {
          this.setCurrentLevelIvFrame2({ levelNumber: this.selectedLevelIvFrame1 })
        }
        this.secondarySrc = formattedUrl
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use 'src/design-system/design-tokens/variables.scss';
@import 'src/styles/variables.scss';

.app-indoorviewer {
  display: flex;
  height: calc(100% - #{variables.$layout-top-bar-height});
  margin-top: #{variables.$layout-top-bar-height};
  justify-content: space-between;
  max-width: $width-mapviewer;
  width: calc($width-indoorviewer + 1px); // The + 1 px is in order to cover half pixels
}
.floor-buttons-frame1 {
  display: flex;
  &--split-indoorviewer {
    left: calc((#{$width-indoorviewer} * 0.5) - 56px);
    width: fit-content;
  }
}
.floor-buttons-frame2 {
  &--split-indoorviewer {
    left: unset;
    right: 25px;
  }
}
.spinner-overview {
  position: absolute;
  background: #f8f8f8;
  display: flex;
  height: calc(#{100vh} - #{variables.$layout-top-bar-height});
  width: $width-indoorviewer;
  align-items: center;
  justify-content: center;
  &__split1 {
    width: calc((#{$width-indoorviewer} * 0.5) - 2px);
  }
  &__split2 {
    width: calc((#{$width-indoorviewer} * 0.5) - 2px);
    left: calc((#{$width-indoorviewer} * 0.5) + 2px);
  }
}
@media screen and (max-width: 1024px) {
  .app-indoorviewer {
    max-width: 100%;
    width: 100%;
    height: calc(100vh - #{variables.$layout-top-bar-height});
    position: relative;
    margin-top: variables.$layout-top-bar-height;
  }

  .modal-dialog {
    margin-top: 64px;
  }

  [id|='indoorviewer-iframe'] {
    height: calc(100% - #{variables.$view-buttons-height});
  }
  .spinner-overview {
    width: $width-indoorviewer;
    &__split1 {
      width: calc((#{$width-indoorviewer} * 0.5) - 2px);
    }
    &__split2 {
      width: calc((#{$width-indoorviewer} * 0.5) - 2px);
      left: calc(((#{$width-indoorviewer}) * 0.5) + 2px);
    }
  }
}
</style>
